/**
 * Single Play Link Animation
 * Creates underline animation that plays once on hover
 */
class SinglePlayLinkAnimation {
    constructor(selector = 'a') {
        this.selector = selector;
        this.animationDuration = 400; // 0.2s + 0.2s delay = 0.4s
        this.init();
    }

    init() {
        const links = document.querySelectorAll(this.selector);

        links.forEach(link => {
            // Remove empty class attribute if it exists
            if (link.hasAttribute('class') && link.classList.length === 0) {
                link.removeAttribute('class');
            }

            // Initialize link state
            link.linkState = {
                isHovered: false,
                timeoutId: null,
                currentPhase: 'idle',
                animationPlayed: false // Track if animation has been played
            };

            link.addEventListener('mouseenter', () => this.startAnimation(link));
            link.addEventListener('mouseleave', () => this.stopAnimation(link));
            link.addEventListener('focus', () => this.startAnimation(link));
            link.addEventListener('blur', () => this.stopAnimation(link));

            // Append icon for external links
            const isExternal = link.hostname && link.hostname !== window.location.hostname;
            if (isExternal && !link.querySelector('.icon-arrow-up-right')) {
                const icon = document.createElement('i');
                icon.classList.add('icon-arrow-up-right');
                icon.style.marginLeft = '5px';
                icon.style.display = 'inline-block';
                icon.style.verticalAlign = 'middle';
                link.appendChild(icon);
            }
        });
    }

    startAnimation(link) {
        const state = link.linkState;
        state.isHovered = true;

        // Only play if animation hasn't been played yet AND not currently animating
        if (!state.animationPlayed && state.currentPhase !== 'animating') {
            // Clear any existing animation
            this.clearAnimation(state);

            // Start the single animation cycle
            this.runSingleCycle(link);
        }
    }

    stopAnimation(link) {
        const state = link.linkState;
        state.isHovered = false;

        // Don't reset if animation is currently running - let it finish
        if (state.currentPhase !== 'animating') {
            state.animationPlayed = false; // Reset for next hover

            // Clear animation and reset immediately without animation
            this.clearAnimation(state);
            this.removeClasses(link);
            state.currentPhase = 'idle';
        }
    }

    runSingleCycle(link) {
        const state = link.linkState;

        if (!state.isHovered) return;

        // Mark animation as played
        state.animationPlayed = true;

        // Just one smooth animation: original line out, new line in
        state.currentPhase = 'animating';
        this.removeClasses(link);
        link.classList.add('animate-out', 'animate-in');

        // Animation complete
        state.timeoutId = setTimeout(() => {
            state.currentPhase = 'complete';

            // If user has hovered out during animation, reset now
            if (!state.isHovered) {
                state.animationPlayed = false;
                this.removeClasses(link);
                state.currentPhase = 'idle';
            }
        }, this.animationDuration * 2); // Total duration for both transitions
    }

    resetToInitial(link) {
        const state = link.linkState;

        this.removeClasses(link);

        // Smooth transition back to initial state
        if (state.currentPhase !== 'idle') {
            link.classList.add('animate-reset');

            setTimeout(() => {
                this.removeClasses(link);
                state.currentPhase = 'idle';
            }, this.animationDuration);
        }
    }

    removeClasses(link) {
        link.classList.remove('animate-out', 'animate-in', 'animate-reset');
        if (link.classList.length === 0) {
            link.removeAttribute('class');
        }
    }

    clearAnimation(state) {
        if (state.timeoutId) {
            clearTimeout(state.timeoutId);
            state.timeoutId = null;
        }
    }

    // Optional: Method to reset all animations (useful for testing or resetting state)
    resetAllAnimations() {
        const links = document.querySelectorAll(this.selector);
        links.forEach(link => {
            if (link.linkState) {
                link.linkState.animationPlayed = false;
                this.resetToInitial(link);
            }
        });
    }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => new SinglePlayLinkAnimation(':is(.title-area__teaser-text a:not(.button), .link, .paragraph__content a:not(.button))'));
